/**
 * SEOHead - client-side meta tag injector
 * Design: Signal & Authority, Editorial Brutalism meets Swiss Typography
 *
 * Injects:
 * - title, meta description, canonical, robots
 * - Open Graph: og:title, og:description, og:url, og:type, og:site_name,
 *   og:image (+width/height/alt), og:locale
 * - Article OG: article:published_time, article:modified_time,
 *   article:author, article:section, article:tag (one per tag)
 * - Twitter/X card: card, title, description, image, site, creator
 * - Keywords meta tag
 */

import { useEffect } from "react";
import { normaliseMetaDescription } from "@/lib/seoMetadata";
import { resolveRouteSocialPreview } from "@/lib/routeSocialPreviews";
import { resolveStaticTitleFix } from "@/lib/titleFixes";
import { getLocalisedRoute, getPublicAlternates } from "@/i18n/localisedRoutes";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical: string;
  /** Absolute URL to the hero/preview image for og:image and twitter:image */
  ogImage?: string;
  /** Descriptive alternative text for the social-preview image. Falls back to the page title. */
  ogImageAlt?: string;
  /** Source-verified social preview MIME type and dimensions. */
  ogImageType?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  /** Pass true for dynamic/thin-content pages (e.g. search results) to prevent indexing */
  noindex?: boolean;
  /** Article-specific meta fields - only used when rendering blog posts */
  article?: {
    publishedTime: string;   // ISO 8601, e.g. "2026-06-25"
    modifiedTime?: string;   // ISO 8601, falls back to publishedTime
    author?: string;         // Author name, e.g. "Modi Elnadi"
    section?: string;        // Primary section/category
    tags?: string[];         // All article tags
  };
  /** Comma-separated keywords for the keywords meta tag */
  keywords?: string;
  /** Page language metadata. Arabic pilot routes remain noindex until approved. */
  locale?: "en" | "en-US" | "ar";
  /** Reciprocal language pairs, emitted only for approved published equivalents. */
  alternates?: Array<{ hrefLang: string; href: string }>;
}

function setMeta(selector: string, attr: string, value: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${selector}"]`);
  if (el) {
    el.setAttribute("content", content);
  } else {
    el = document.createElement("meta");
    el.setAttribute(attr, selector);
    el.setAttribute("content", content);
    document.head.appendChild(el);
  }
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (el) {
    el.setAttribute("href", href);
  } else {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("href", href);
    document.head.appendChild(el);
  }
}

/**
 * Canonical paths on Integrated.Social end in a slash. Preserve query strings
 * and hashes so a self-canonical paginated archive remains `/blog/?page=2`, not
 * the invalid `/blog/?page=2/` generated by appending to the full URL string.
 */
export function normalizeCanonicalUrl(canonical: string) {
  const url = new URL(canonical);
  if (!/\.[a-z]{2,4}$/i.test(url.pathname) && !url.pathname.endsWith("/")) {
    url.pathname = `${url.pathname}/`;
  }
  return url.toString();
}

/** Remove all existing article:tag meta elements before re-injecting */
function clearArticleTags() {
  document.querySelectorAll('meta[property="article:tag"]').forEach((el) => el.remove());
}

export default function SEOHead({
  title,
  description,
  canonical,
  ogImage,
  ogImageAlt,
  ogImageType,
  ogImageWidth,
  ogImageHeight,
  noindex = false,
  article,
  keywords,
  locale = "en",
  alternates = [],
}: SEOHeadProps) {
  const resolvedTitle = resolveStaticTitleFix(canonical, title);
  const normalizedDescription = normaliseMetaDescription(description, resolvedTitle);
  const socialPreviewImage = resolveRouteSocialPreview(canonical, ogImage);

  useEffect(() => {
    // ── Title ──────────────────────────────────────────────────────────────────
    document.title = resolvedTitle;
    document.documentElement.lang = locale === "ar" ? "ar" : locale === "en-US" ? "en-US" : "en-GB";
    document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";

    // ── Meta description ───────────────────────────────────────────────────────
    setMeta("description", "name", "description", normalizedDescription);

    // ── Keywords ──────────────────────────────────────────────────────────────
    if (keywords) {
      setMeta("keywords", "name", "keywords", keywords);
    }

    // ── Canonical ─────────────────────────────────────────────────────────────
    // Normalise: ensure trailing slash on every canonical URL so it matches the
    // server's canonical form and avoids Ahrefs "canonical points to redirect" warnings.
    // The path, rather than the whole URL string, is normalized so valid query
    // canonicals such as `/blog/?page=2` remain unchanged after hydration.
    const normalizedCanonical = normalizeCanonicalUrl(canonical);
    setLink("canonical", normalizedCanonical);

    // ── Robots ────────────────────────────────────────────────────────────────
    const robotsContent = noindex ? "noindex, follow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1";
    setMeta("robots", "name", "robots", robotsContent);

    // ── Open Graph core ───────────────────────────────────────────────────────
    setMeta("og:title", "property", "og:title", resolvedTitle);
    setMeta("og:description", "property", "og:description", normalizedDescription);
    setMeta("og:url", "property", "og:url", normalizedCanonical);
    setMeta("og:type", "property", "og:type", article ? "article" : "website");
    setMeta("og:site_name", "property", "og:site_name", "Integrated.Social");
    setMeta("og:locale", "property", "og:locale", locale === "ar" ? "ar_AR" : locale === "en-US" ? "en_US" : "en_GB");
    const canonicalPath = new URL(normalizedCanonical).pathname;
    const frozenLocal = canonicalPath === "/local/" || canonicalPath.startsWith("/local/");
    const resolvedAlternates = frozenLocal || alternates.length > 0 ? alternates
      : getPublicAlternates(getLocalisedRoute(canonicalPath)).map(pair => ({
        hrefLang: pair.hrefLang,
        href: new URL(pair.path, normalizedCanonical).toString(),
      }));
    document.querySelectorAll(frozenLocal
      ? 'meta[data-seo-og-alternate="true"]'
      : 'meta[property="og:locale:alternate"]'
    ).forEach((el) => el.remove());
    if (locale === "ar") {
      const alternate = document.createElement("meta");
      alternate.setAttribute("property", "og:locale:alternate");
      alternate.setAttribute("content", resolvedAlternates.some(pair => pair.hrefLang === "en-US") ? "en_US" : "en_GB");
      alternate.setAttribute("data-seo-og-alternate", "true");
      document.head.appendChild(alternate);
    }

    document.querySelectorAll(frozenLocal
      ? 'link[data-seo-alternate="true"]'
      : 'link[rel="alternate"][hreflang]'
    ).forEach((el) => el.remove());
    const uniqueAlternates = resolvedAlternates.filter((alternate, index, pairs) =>
      pairs.findIndex(pair => pair.hrefLang === alternate.hrefLang && pair.href === alternate.href) === index
    );
    uniqueAlternates.forEach((alternate) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hrefLang", alternate.hrefLang);
      link.setAttribute("href", alternate.href);
      link.setAttribute("data-seo-alternate", "true");
      document.head.appendChild(link);
    });

    if (socialPreviewImage) {
      const socialImageAlt = ogImageAlt ?? resolvedTitle;
      setMeta("og:image", "property", "og:image", socialPreviewImage);
      setMeta("og:image:width", "property", "og:image:width", String(ogImageWidth ?? 1200));
      setMeta("og:image:height", "property", "og:image:height", String(ogImageHeight ?? 630));
      setMeta("og:image:alt", "property", "og:image:alt", socialImageAlt);
      // Derive MIME type from URL extension - LinkedIn requires JPEG or PNG (rejects WebP)
      const resolvedOgImageType = ogImageType ?? (socialPreviewImage.endsWith(".png")
        ? "image/png"
        : socialPreviewImage.endsWith(".webp")
        ? "image/webp"
        : socialPreviewImage.endsWith(".gif")
        ? "image/gif"
        : "image/jpeg");
      setMeta("og:image:type", "property", "og:image:type", resolvedOgImageType);
    }

    // ── Article-specific Open Graph ───────────────────────────────────────────
    if (article) {
      setMeta("article:published_time", "property", "article:published_time", article.publishedTime);
      setMeta("article:modified_time", "property", "article:modified_time", article.modifiedTime ?? article.publishedTime);
      if (article.author) {
        setMeta("article:author", "property", "article:author", article.author);
      }
      if (article.section) {
        setMeta("article:section", "property", "article:section", article.section);
      }
      // article:tag - one meta element per tag (standard OG spec)
      if (article.tags && article.tags.length > 0) {
        clearArticleTags();
        article.tags.forEach((tag) => {
          const el = document.createElement("meta");
          el.setAttribute("property", "article:tag");
          el.setAttribute("content", tag);
          document.head.appendChild(el);
        });
      }
    }

    // ── Twitter / X card ──────────────────────────────────────────────────────
    setMeta("twitter:card", "name", "twitter:card", socialPreviewImage ? "summary_large_image" : "summary");
    setMeta("twitter:title", "name", "twitter:title", resolvedTitle);
    setMeta("twitter:description", "name", "twitter:description", normalizedDescription);
    setMeta("twitter:site", "name", "twitter:site", "@IntegratedSocial");
    setMeta("twitter:creator", "name", "twitter:creator", "@MoElnadi");
    if (socialPreviewImage) {
      setMeta("twitter:image", "name", "twitter:image", socialPreviewImage);
      setMeta("twitter:image:alt", "name", "twitter:image:alt", ogImageAlt ?? resolvedTitle);
    }
  }, [resolvedTitle, normalizedDescription, canonical, socialPreviewImage, ogImageAlt, noindex, article, keywords, locale, alternates, ogImageType, ogImageWidth, ogImageHeight]);

  return null;
}
